import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Info, Calculator, Wrench, Settings, HelpCircle } from 'lucide-react';
import { IMAGES, bgStyle } from '@/config/images';

const WhatWeDo = () => {
  return (
    <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" style={bgStyle(IMAGES.CONTACT_FEEDBACK)}>
      <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
      <div className="max-w-3xl mx-auto">
        {/* Back Link */}
        <Link to="/" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-orange-500/20 rounded-lg">
              <Info className="w-8 h-8 text-orange-500" />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-orange-500 mb-1">ABOUT OUR SERVICE</div>
              <h1 
                className="text-3xl md:text-4xl font-bold text-orange-500 tracking-tight uppercase"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                What CNC-Helpdesk Does
              </h1>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-black/70 backdrop-blur-sm border border-zinc-800 rounded-lg p-8">
          <div className="prose prose-invert max-w-none">
            <p className="text-lg text-zinc-300 leading-relaxed mb-6">
              <strong className="text-orange-500">CNC-Helpdesk</strong> is an educational software platform that provides guidance, calculators, machine setup workflows, and troubleshooting assistance for CNC routers, 3D printers, and laser machines.
            </p>

            {/* Key Features */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-8">
              <div className="bg-zinc-800/50 p-4 rounded-lg border border-zinc-700">
                <div className="flex items-center gap-3 mb-2">
                  <Calculator className="w-5 h-5 text-orange-500" />
                  <span className="font-semibold text-white">Smart Calculators</span>
                </div>
                <p className="text-sm text-zinc-400">Feeds & speeds, material settings, and parameter optimization</p>
              </div>
              
              <div className="bg-zinc-800/50 p-4 rounded-lg border border-zinc-700">
                <div className="flex items-center gap-3 mb-2">
                  <Settings className="w-5 h-5 text-orange-500" />
                  <span className="font-semibold text-white">Machine Setup</span>
                </div>
                <p className="text-sm text-zinc-400">Step-by-step configuration workflows for your equipment</p>
              </div>
              
              <div className="bg-zinc-800/50 p-4 rounded-lg border border-zinc-700">
                <div className="flex items-center gap-3 mb-2">
                  <Wrench className="w-5 h-5 text-orange-500" />
                  <span className="font-semibold text-white">Troubleshooting</span>
                </div>
                <p className="text-sm text-zinc-400">Diagnose and fix common issues with guided solutions</p>
              </div>
              
              <div className="bg-zinc-800/50 p-4 rounded-lg border border-zinc-700">
                <div className="flex items-center gap-3 mb-2">
                  <HelpCircle className="w-5 h-5 text-orange-500" />
                  <span className="font-semibold text-white">AI Assistance</span>
                </div>
                <p className="text-sm text-zinc-400">Get personalized help from our AI troubleshooting assistant</p>
              </div>
            </div>

            {/* Important Notices */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-6 my-8">
              <h3 className="text-lg font-semibold text-blue-400 mb-4">Important Information</h3>
              <ul className="space-y-3 text-zinc-300">
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>CNC-Helpdesk does <strong>NOT</strong> operate hardware, control machines, or automate any physical equipment.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>All content is <strong>informational</strong> and intended for safe and responsible machine use.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>Users are responsible for verifying all settings against their specific machine specifications.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>This service is <strong>NOT</strong> a substitute for professional training or manufacturer guidelines.</span>
                </li>
              </ul>
            </div>

            {/* Supported Disciplines */}
            <h3 className="text-xl font-semibold text-white mb-4">Supported Disciplines</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="text-center p-4 bg-zinc-800/30 rounded-lg border border-zinc-700">
                <div className="text-2xl mb-2">🔧</div>
                <div className="font-semibold text-white">CNC Routing</div>
                <div className="text-xs text-zinc-500">Wood, Plastic, Aluminum</div>
              </div>
              <div className="text-center p-4 bg-zinc-800/30 rounded-lg border border-zinc-700">
                <div className="text-2xl mb-2">🖨️</div>
                <div className="font-semibold text-white">3D Printing</div>
                <div className="text-xs text-zinc-500">FDM, Filaments, Slicers</div>
              </div>
              <div className="text-center p-4 bg-zinc-800/30 rounded-lg border border-zinc-700">
                <div className="text-2xl mb-2">⚡</div>
                <div className="font-semibold text-white">Laser Cutting</div>
                <div className="text-xs text-zinc-500">CO2, Diode, Engraving</div>
              </div>
            </div>

            <p className="text-zinc-400 text-sm">
              For questions about our service, please contact: <a href="mailto:Andre@cnc-helpdesk.com" className="text-orange-500 hover:underline">Andre@cnc-helpdesk.com</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatWeDo;
